import { createContext, useContext } from 'react';

import type { EditorContentUnifiedQuery$data } from './__generated__/EditorContentUnifiedQuery.graphql.ts';

type EditorContentData = EditorContentUnifiedQuery$data;

export type EditorContentNode = NonNullable<
	NonNullable<NonNullable<EditorContentData['content']>['nodes']>[number]
>;

// The query selects only aliased fragment spreads under the node and on Query besides content, so
// their keys are the fragments.
type EditorContentAliasedRefs = EditorContentNode & Omit<EditorContentData, 'content'>;

export type EditorContentFragmentKey = keyof EditorContentAliasedRefs;

export type EditorContentFragmentRef<K extends EditorContentFragmentKey> = NonNullable<
	EditorContentAliasedRefs[K]
>;

const getNode = (data: EditorContentData): EditorContentNode | null | undefined =>
	data.content?.nodes?.[0];

// Where each fragment's ref is in the operation's data; adding a fragment to the query without a
// selector here fails to compile.
const EDITOR_CONTENT_FRAGMENT_SELECTORS: {
	readonly [K in EditorContentFragmentKey]: (
		data: EditorContentData,
	) => EditorContentFragmentRef<K> | null | undefined;
} = {
	contentTypesHeaderFragment: (data) => getNode(data)?.contentTypesHeaderFragment,
	emojiTitleFragment: (data) => getNode(data)?.emojiTitleFragment,
	emojiEverSetPropertyFragment: (data) => getNode(data)?.emojiEverSetPropertyFragment,
	contentAppearanceFragment: (data) => getNode(data)?.contentAppearanceFragment,
	coverPictureFragment: (data) => getNode(data)?.coverPictureFragment,
	titleContentPropertiesFragment: (data) => getNode(data)?.titleContentPropertiesFragment,
	contentModeFragment: (data) => getNode(data)?.contentModeFragment,
	shareAndRestrictButtonFragment: (data) => getNode(data)?.shareAndRestrictButtonFragment,
	editorSpaceDetailFragment: (data) => getNode(data)?.editorSpaceDetailFragment,
	nativeCollabFragment: (data) => getNode(data)?.nativeCollabFragment,
	labelsFragment: (data) => getNode(data)?.labelsFragment,
	smartCreateFeedbackBannerFragment: (data) => getNode(data)?.smartCreateFeedbackBannerFragment,
	favouriteContentFragment: (data) => getNode(data)?.favouriteContentFragment,
	migratedRecentlyFragment: (data) => getNode(data)?.migratedRecentlyFragment,
	editorByLineFragment: (data) => getNode(data)?.editorByLineFragment,
	devDocsAncestorsFragment: (data) => getNode(data)?.devDocsAncestorsFragment,
	editorContentStateFragment: (data) => getNode(data)?.editorContentStateFragment,
	shareAndRestrictButtonPublicLinkFragment: (data) => data.shareAndRestrictButtonPublicLinkFragment,
	editorByLineContributorsFragment: (data) => data.editorByLineContributorsFragment,
	editorAvailableContentStatesFragment: (data) => data.editorAvailableContentStatesFragment,
	editorCollaboratorsFragment: (data) => getNode(data)?.editorCollaboratorsFragment,
	editorCollaboratorsListFragment: (data) => data.editorCollaboratorsListFragment,
	editorAttachmentDownloadPolicyFragment: (data) => data.editorAttachmentDownloadPolicyFragment,
};

// contentId: the document the container shows. loading: ref is still the previous document's
// while that one loads.
export type EditorContentFragment<K extends EditorContentFragmentKey> =
	| {
			isProvided: true;
			ref: EditorContentFragmentRef<K> | null;
			contentId: string;
			loading: boolean;
	  }
	| { isProvided: false };

export type EditorContentFragments = {
	readonly [K in EditorContentFragmentKey]: EditorContentFragment<K>;
};

const EDITOR_CONTENT_FRAGMENT_KEYS = Object.keys(
	EDITOR_CONTENT_FRAGMENT_SELECTORS,
) as EditorContentFragmentKey[];

function mapFragments(
	toFragment: (key: EditorContentFragmentKey) => EditorContentFragment<EditorContentFragmentKey>,
): EditorContentFragments {
	return Object.fromEntries(
		EDITOR_CONTENT_FRAGMENT_KEYS.map((key) => [key, toFragment(key)]),
	) as EditorContentFragments;
}

/**
 * The fragments of EditorContentUnifiedQuery's data, every one of them provided even when the
 * operation returns no node, so its readers render no data instead of running their queries.
 */
export function createEditorContentFragments(
	data: EditorContentData,
	{ contentId, loading }: { contentId: string; loading: boolean },
): EditorContentFragments {
	return mapFragments((key) => ({
		isProvided: true,
		ref: EDITOR_CONTENT_FRAGMENT_SELECTORS[key](data) ?? null,
		contentId,
		loading,
	}));
}

const NOT_PROVIDED_FRAGMENTS: EditorContentFragments = mapFragments(() => ({ isProvided: false }));

export const EditorContentFragmentsContext: React.Context<EditorContentFragments> =
	createContext<EditorContentFragments>(NOT_PROVIDED_FRAGMENTS);

/**
 * The fragments of the editor document. One is not provided outside EditorContentContainer
 * (embedded, template and smart create editors, renderers) and with the experiment off, where its
 * reader keeps its own query; when provided, ref is null if the operation returns no node.
 */
export function useEditorContentFragments(): EditorContentFragments {
	return useContext(EditorContentFragmentsContext);
}
